import { useMemo } from 'react';
import { LogoMark } from '@cbhq/cds-web/icons/LogoMark';
import { Avatar } from '@cbhq/cds-web/media/Avatar';
import { SectionComponentLayout, Div, ArticleSections, PillTags, Text, RichText } from '@cbhq/cdx-components';
import { useContentfulMicrocopy } from '../../globals/microcopy.js';
import { mapEntryLink } from '../../globals/navlink.js';
import { getEntryProps } from '../../utils/getEntryProps.js';
import { authorBioFormatOptions } from './authorBioFormatOptions.js';
import { jsx, jsxs } from 'react/jsx-runtime';
const EDITORIAL_CONTENT_MAX_WIDTH = '700px';
function EditorialBody({
  content,
  authors = [],
  tags = [],
  richTextOptions,
  isEmbeddedView = false
}) {
  const pillTags = useMemo(() => {
    return tags.filter(tag => tag.fields.type !== 'internal').map(tag => {
      const _getEntryProps = getEntryProps(tag),
        label = _getEntryProps.label,
        link = _getEntryProps.link;
      const tagLink = mapEntryLink(link);
      return {
        label,
        url: tagLink.url && `/${tagLink.url}`
      };
    });
  }, [tags]);
  const authorsRender = authors.filter(author => !!author.fields.authorBioRichText).map(({
    fields: {
      authorName,
      authorBioRichText,
      authorImage
    }
  }) => ({
    name: authorName,
    bio: authorBioRichText,
    photoUrl: authorImage?.fields?.file?.url
  }));
  return /*#__PURE__*/jsx(SectionComponentLayout, {
    hasSpacingHorizontal: true,
    children: /*#__PURE__*/jsxs(Div, {
      flexDirection: "column",
      maxWidth: isEmbeddedView ? undefined : EDITORIAL_CONTENT_MAX_WIDTH,
      children: [/*#__PURE__*/jsx(ArticleSections, {
        content: content,
        richTextOptions: richTextOptions
      }), content && /*#__PURE__*/jsx(Div, {
        spacingTop: 5,
        children: /*#__PURE__*/jsx(LogoMark, {
          size: 32
        })
      }), pillTags.length > 0 && /*#__PURE__*/jsx(Div, {
        flexDirection: "column",
        spacingTop: 8,
        gap: 2,
        children: /*#__PURE__*/jsx(PillTags, {
          tags: pillTags
        })
      }), authorsRender?.length > 0 && /*#__PURE__*/jsx(Div, {
        spacingTop: 8,
        children: /*#__PURE__*/jsx(AuthorsBio, {
          authors: authorsRender
        })
      })]
    })
  });
}
function AuthorsBio({
  authors = []
}) {
  const microcopies = useContentfulMicrocopy();
  const aboutAuthorsText = authors.length === 1 ? authors?.[0].name : `${authors.map(author => author.name).slice(0, authors.length - 1).join(', ')} ${microcopies.global?.and} ${authors[authors.length - 1].name}`;
  return /*#__PURE__*/jsxs(Div, {
    flexDirection: "column",
    children: [/*#__PURE__*/jsxs(Div, {
      gap: 2,
      flexWrap: "wrap",
      children: [authors.map(({
        name,
        photoUrl
      }, index) => /*#__PURE__*/jsx(Avatar, {
        alt: name,
        src: photoUrl,
        size: "xxl"
      }, index)), /*#__PURE__*/jsx(Div, {
        flexDirection: "column",
        justifyContent: "center",
        children: /*#__PURE__*/jsxs(Text, {
          variant: "label1",
          as: "p",
          children: [microcopies.global?.about, " ", aboutAuthorsText]
        })
      })]
    }), authors.map(({
      bio
    }, index) => {
      if (bio) return /*#__PURE__*/jsx(Div, {
        flexDirection: "column",
        spacingTop: 2,
        children: /*#__PURE__*/jsx(RichText, {
          richTextOptions: authorBioFormatOptions,
          content: bio
        })
      }, index);
    })]
  });
}
export { AuthorsBio, EDITORIAL_CONTENT_MAX_WIDTH, EditorialBody };
//# sourceMappingURL=EditorialBody.js.map
